import { useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { cloneDeep } from 'es-toolkit';
import { validate } from 'validate.js';

import { Alert, Box, Form, Heading, List, Modal, Paragraph, Toast, useToast } from '@zillow/constellation-10';

import { logError } from '@zg-rentals/log-error';
import { useQueryClient } from '@zg-rentals/react-query5';
import { DatarouterError } from '@zg-rentals/rental-manager-env';
import { FeeCalculationType, FeeTypeGrouping } from '@zg-rentals/ts-rental-manager-api-beta-client';
import {
  useRentalManagerUserPropertySingleFamilyCostAndFeesWebApiHandler_create,
  useRentalManagerUserPropertySingleFamilyCostAndFeesWebApiHandler_update,
} from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import {
  getSingleFamilyCostsAndFeesKey,
  useSingleFamilyCostsAndFees,
} from '../../../../srcLegacy/shared/hooks/queries/useSingleFamilyCostsAndFees';
import { removeErrorKey } from '../../../../srcLegacy/shared/utils/formValidation';

import FeeAmountField from '../../../../srcLegacy/shared/components/CostsAndFeesFields/FeeAmountField';
import FeeDropdownSelectField from '../../../../srcLegacy/shared/components/CostsAndFeesFields/FeeDropdownSelectField';
import FeeInputField from '../../../../srcLegacy/shared/components/CostsAndFeesFields/FeeInputField';
import FeeRadioGroupField from '../../../../srcLegacy/shared/components/CostsAndFeesFields/FeeRadioGroupField';

import { costsAndFeesConstraints } from '../../../../srcLegacy/shared/validation';
import ModalFooter from '../ModalFooter';

import type React from 'react';
import type {
  CreateSingleFamilyFeesRequestDto,
  FeeCalculationDictionaryItemDisplayDto,
  FeeCategoryDictionaryItemDto,
  FeeDictionaryItemDisplayDto,
  FeeRefundableType,
  FeeRequirementType,
  FeeTimingSubType,
  FeeType,
  GetSingleFamilyFeesResponseDto,
  MultiErrorsResponseDto,
  SingleFamilyFeeDto,
} from '@zg-rentals/ts-rental-manager-api-beta-client';
import type { UpdateSingleFamilyFeesRequestDto } from '@zg-rentals/ts-rental-manager-api-beta-client';

type Props = {
  onClose: () => void;
  feeToEdit?: SingleFamilyFeeDto | null;
  fixedCategory: string;
};

const formatCreateOrUpdateErrors = (generalMessage: string, errors: Array<{ message: string }>) => (
  <>
    <Paragraph>{generalMessage}</Paragraph>
    <List.Root>
      {errors.map((error) => (
        <List.Item key={error.message}>{error.message}</List.Item>
      ))}
    </List.Root>
  </>
);

const SingleFamilyCostsAndFeesModal = ({ onClose, feeToEdit = null, fixedCategory = '' }: Props) => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string;

  const queryClient = useQueryClient();
  const topRef = useRef<HTMLDivElement>(null);

  const initialFeeAmountMax =
    feeToEdit?.feeAmountMax &&
    (feeToEdit?.feeCalculationType === FeeCalculationType.RANGE ||
      feeToEdit?.feeCalculationType === FeeCalculationType.PER_ITEM_RANGE)
      ? feeToEdit.feeAmountMax
      : 0;
  const [feeAmountMax, setFeeAmountMax] = useState(initialFeeAmountMax);
  const [feeAmountMin, setFeeAmountMin] = useState(feeToEdit?.feeAmountMin || (0 as number));
  const [feeCategory, setFeeCategory] = useState(fixedCategory || '');
  const [feeType, setFeeType] = useState(feeToEdit?.feeType || '');
  const [feeDescription, setFeeDescription] = useState(feeToEdit?.feeDescription || '');
  const [feePaymentFrequencyType, setFeePaymentFrequencyType] = useState(feeToEdit?.feePaymentFrequencyType || '');
  const [feeRefundableType, setFeeRefundableType] = useState(feeToEdit?.feeRefundableType || '');
  const [feeRequirementType, setFeeRequirementType] = useState(feeToEdit?.feeRequirementType || '');
  const [feeFormat, setFeeFormat] = useState(feeToEdit?.feeCalculationType || '');

  const [feeErrors, setFeeErrors] = useState<Record<string, string>>({});
  const [feeCreateOrUpdateError, setFeeCreateOrUpdateError] = useState<React.ReactNode | null>(null);

  const { enqueueToast } = useToast();

  const showFeeError = (baseMessage: string, errors?: Array<{ message: string }>) => {
    if (errors?.length) {
      setFeeCreateOrUpdateError(formatCreateOrUpdateErrors(`${baseMessage} Error details:`, errors));
    } else {
      setFeeCreateOrUpdateError(baseMessage);
    }
    topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const { data } = useSingleFamilyCostsAndFees({ listingAlias, enabled: !!listingAlias });

  const mainCategoriesKeys = Object.keys(data!.dictionaryItems.mainCategories);

  const { mutate: createFee, isPending: isCreatingFee } =
    useRentalManagerUserPropertySingleFamilyCostAndFeesWebApiHandler_create({
      onSuccess: (response: MultiErrorsResponseDto<SingleFamilyFeeDto>) => {
        const result = response.result;

        const responseErrors = response.errors as Array<{ message: string }>;
        if (responseErrors?.length) {
          showFeeError('Your new fee couldn’t be added. Refresh the page and try again.', responseErrors);
          return;
        }

        const queryKey = getSingleFamilyCostsAndFeesKey({ listingAlias });

        queryClient.setQueryData<GetSingleFamilyFeesResponseDto>(
          queryKey,
          (prevFeesData?: GetSingleFamilyFeesResponseDto) => {
            if (!prevFeesData) {
              return {} as GetSingleFamilyFeesResponseDto;
            }

            const feesData = cloneDeep(prevFeesData);

            if (feesData?.existingFees) {
              if (Object.keys(feesData.dictionaryItems.mainCategories).includes(result.feeCategory)) {
                const category = result.feeCategory as keyof typeof feesData.existingFees.mainCategories;
                const existingFeeCategory = feesData!.existingFees.mainCategories[category];
                if (existingFeeCategory) {
                  feesData!.existingFees.mainCategories[category].push(result);
                } else {
                  feesData.existingFees.mainCategories[category] = [result];
                }
              } else {
                feesData.existingFees.otherCategories.push(result);
              }
            }

            return feesData as GetSingleFamilyFeesResponseDto;
          },
        );

        onClose();
        enqueueToast(<Toast tone="success">New fee added</Toast>);
      },
      onError: (err: unknown) => {
        logError({
          error: err,
          context: {
            message: `Failed to create fee for listingAlias: ${listingAlias}`,
            details: 'Create single-family fee',
          },
        });

        const { errors } = DatarouterError.getErrorDetails(err);
        showFeeError('Your new fee couldn’t be added. Refresh the page and try again.', errors);
      },
      meta: {
        invalidates: ['sf-costsAndFees'],
        context: { api: 'rental-manager', details: 'create single-family fee' },
      },
    });

  const { mutate: updateFee, isPending: isUpdatingFee } =
    useRentalManagerUserPropertySingleFamilyCostAndFeesWebApiHandler_update({
      onSuccess: (response: MultiErrorsResponseDto<SingleFamilyFeeDto>) => {
        const result = response.result;

        const responseErrors = response.errors as Array<{ message: string }>;
        if (responseErrors?.length) {
          showFeeError('Your changes couldn’t be saved. Refresh the page and try again.', responseErrors);
          return;
        }

        const queryKey = getSingleFamilyCostsAndFeesKey({ listingAlias });

        queryClient.setQueryData<GetSingleFamilyFeesResponseDto>(
          queryKey,
          (prevFeesData?: GetSingleFamilyFeesResponseDto) => {
            if (!prevFeesData) {
              return {} as GetSingleFamilyFeesResponseDto;
            }

            const feesData = cloneDeep(prevFeesData);

            if (Object.keys(feesData.dictionaryItems.mainCategories).includes(result.feeCategory)) {
              const category = result.feeCategory as keyof typeof feesData.existingFees.mainCategories;
              const existingFeeCategory = feesData!.existingFees.mainCategories[category];
              const existingFeeIndex = existingFeeCategory.findIndex(
                (existingFee) => existingFee.costAndFeeId === result.costAndFeeId,
              );
              if (existingFeeIndex > -1) {
                existingFeeCategory[existingFeeIndex] = result;
              }
            } else {
              const existingFeeIndex = feesData.existingFees.otherCategories.findIndex(
                (existingFee) => existingFee.costAndFeeId === result.costAndFeeId,
              );
              if (existingFeeIndex > -1) {
                feesData.existingFees.otherCategories[existingFeeIndex] = result;
              }
            }

            return feesData as GetSingleFamilyFeesResponseDto;
          },
        );

        onClose();
        enqueueToast(<Toast tone="success">Changes saved</Toast>);
      },
      onError: (err: unknown) => {
        logError({
          error: err,
          context: {
            message: `Failed to update fee for listingAlias: ${listingAlias}, costAndFeeId: ${feeToEdit?.costAndFeeId}`,
            details: 'Update single-family fee',
          },
        });

        const { errors } = DatarouterError.getErrorDetails(err);
        showFeeError('Your changes couldn’t be saved. Refresh the page and try again.', errors);
      },
      meta: {
        invalidates: ['sf-costsAndFees'],
        context: { api: 'rental-manager', details: 'update single-family fee' },
      },
    });

  const validateField = (fieldName: string, fieldValue: string | number) => {
    let error = '';
    let feeErrorObj = feeErrors;
    const maxGreaterThanMinErrorMessage = 'Maximum amount must be greater than minimum amount';

    if (fieldName === 'feeFormat') {
      feeErrorObj = {
        ...feeErrorObj,
        feeAmountMin: '',
        feeAmountMax: '',
        feeAmountMinFlat: '',
        feeAmountPercent: '',
        feeAmountMultiple: '',
      };
    }

    if (feeFormat === FeeCalculationType.RANGE && (fieldName === 'feeAmountMin' || fieldName === 'feeAmountMax')) {
      feeErrorObj = {
        ...feeErrorObj,
        feeAmountMin: feeErrors.feeAmountMin === maxGreaterThanMinErrorMessage ? '' : feeErrors.feeAmountMin,
        feeAmountMax: feeErrors.feeAmountMax === maxGreaterThanMinErrorMessage ? '' : feeErrors.feeAmountMax,
      };
      if (
        (fieldName === 'feeAmountMin' && feeAmountMax <= (fieldValue as number)) ||
        (fieldName === 'feeAmountMax' && feeAmountMin >= (fieldValue as number))
      ) {
        error = maxGreaterThanMinErrorMessage;
      } else {
        let errorMin = '';
        let errorMax = '';
        const constraintsForMin = costsAndFeesConstraints.values?.['feeAmountMinRange'];
        if (constraintsForMin) {
          const validationErrorMin = validate(
            { ['feeAmountMinRange']: feeAmountMin },
            { ['feeAmountMinRange']: constraintsForMin },
          )?.['feeAmountMinRange']?.[0];
          if (validationErrorMin) {
            errorMin = removeErrorKey('feeAmountMinRange', validationErrorMin);
          }
        }

        const constraintsForMax = costsAndFeesConstraints.values?.['feeAmountMax'];
        if (constraintsForMax) {
          const validationErrorMax = validate(
            { ['feeAmountMax']: feeAmountMax },
            { ['feeAmountMax']: constraintsForMax },
          )?.['feeAmountMax']?.[0];
          if (validationErrorMax) {
            errorMax = removeErrorKey('feeAmountMax', validationErrorMax);
          }
        }

        return setFeeErrors({
          ...feeErrorObj,
          feeAmountMin: errorMin,
          feeAmountMax: errorMax,
        });
      }
    } else {
      const constraintsForField = costsAndFeesConstraints.values?.[fieldName];
      if (constraintsForField) {
        const validationError = validate({ [fieldName]: fieldValue }, { [fieldName]: constraintsForField })?.[
          fieldName
        ]?.[0];
        if (validationError) {
          error = removeErrorKey(fieldName, validationError);
        }
      }
    }
    setFeeErrors({
      ...feeErrorObj,
      [fieldName]: error,
    });
  };

  const handleFeeCategoryChange = (value: string) => {
    setFeeCategory(value);
    setFeeType('');
  };

  const handleFeeFormatChange = (value: string) => {
    setFeeFormat(value);
    setFeeAmountMax(0);

    if (value === FeeCalculationType.VARIABLE_USAGE_BASED) {
      setFeeAmountMin(0);
    }

    validateField('feeFormat', value);
  };

  const canSubmit = useMemo<boolean>(() => {
    switch (true) {
      case feeFormat === FeeCalculationType.RANGE && (!feeAmountMax || !feeAmountMin):
        return false;
      case feeFormat !== FeeCalculationType.FLAT_FEE &&
        feeFormat !== FeeCalculationType.VARIABLE_USAGE_BASED &&
        feeAmountMin === 0:
        return false;
      case isNaN(feeAmountMin) ||
        isNaN(feeAmountMax) ||
        !feeFormat ||
        !feeCategory ||
        !feeType ||
        !feePaymentFrequencyType ||
        !feeRequirementType:
        return false;
      case Object.values(feeErrors).some((error) => error !== ''):
        return false;
      default:
        return true;
    }
  }, [
    feeAmountMin,
    feeAmountMax,
    feeFormat,
    feeCategory,
    feeType,
    feePaymentFrequencyType,
    feeRequirementType,
    feeErrors,
  ]);

  const onSubmit = () => {
    const postData = {
      feeCalculationType: feeFormat as FeeCalculationType,
      feeCategory: feeCategory as FeeTypeGrouping,
      feeType: feeType as FeeType,
      feePaymentFrequencyType: feePaymentFrequencyType as FeeTimingSubType,
      feeRequirementType: feeRequirementType as FeeRequirementType,
      listingAlias,
    } as CreateSingleFamilyFeesRequestDto;

    if (feeFormat !== FeeCalculationType.VARIABLE_USAGE_BASED) {
      postData.feeAmountMin = feeAmountMin;
      postData.feeAmountMax = feeAmountMax || feeAmountMin;
    }

    if (feeRefundableType !== '') {
      postData.feeRefundableType = feeRefundableType as FeeRefundableType;
    }

    if (feeDescription !== '') {
      postData.feeDescription = feeDescription;
    }

    if (feeToEdit) {
      const updateData: UpdateSingleFamilyFeesRequestDto = {
        ...postData,
        costAndFeeId: feeToEdit.costAndFeeId,
      };
      updateFee({ postData: updateData });
    } else {
      createFee({ postData });
    }
  };

  let modalHeading;
  switch (true) {
    case !!feeToEdit:
      modalHeading = 'Edit a fee';
      break;
    case fixedCategory === FeeTypeGrouping.ADMIN:
      modalHeading = `Add an administrative fee`;
      break;
    case fixedCategory === FeeTypeGrouping.UTILITIES:
      modalHeading = `Add a utilities fee`;
      break;
    case fixedCategory === FeeTypeGrouping.PARKING:
      modalHeading = `Add a parking fee`;
      break;
    default:
      modalHeading = 'Add a fee';
  }

  return (
    <Modal
      open={true}
      onOpenChange={() => onClose()}
      header={<Heading level={2}>{modalHeading}</Heading>}
      body={
        <Box css={{ paddingBottom: '400' }}>
          <div ref={topRef} />
          <Paragraph css={{ marginBottom: '600' }}>
            Let renters know the fee type and amount, when it&apos;s charged, and whether it&apos;s included in the base
            rent. These details will appear on your listing.
          </Paragraph>

          {feeCreateOrUpdateError && (
            <Alert tone="critical" css={{ marginBottom: '400' }}>
              {feeCreateOrUpdateError}
            </Alert>
          )}

          <Form asChild={true} onSubmit={(e) => e.preventDefault()}>
            <div>
              {fixedCategory !== FeeTypeGrouping.ADMIN &&
                fixedCategory !== FeeTypeGrouping.UTILITIES &&
                fixedCategory !== FeeTypeGrouping.PARKING && (
                  <FeeDropdownSelectField
                    value={feeCategory}
                    onChange={handleFeeCategoryChange}
                    dictionaryItems={
                      mainCategoriesKeys.includes(feeCategory as string)
                        ? (data?.dictionaryItems?.mainCategories as Record<string, FeeCategoryDictionaryItemDto>)
                        : (data?.dictionaryItems?.otherCategories.categories as Record<
                            string,
                            FeeCategoryDictionaryItemDto
                          >)
                    }
                    fieldLabel="Category"
                    required={true}
                    onBlur={validateField}
                    errorMessage={feeErrors.feeCategory ?? ''}
                    fieldName="feeCategory"
                    hasIcon={true}
                    formHelpText={fixedCategory === '' ? 'Select the category your fee should go in' : ''}
                    readOnly={feeToEdit !== null}
                  />
                )}

              <FeeDropdownSelectField
                required={true}
                value={feeType}
                fieldLabel="Fee name"
                disabled={feeCategory === ''}
                onChange={setFeeType}
                dictionaryItems={
                  mainCategoriesKeys.includes(feeCategory as string)
                    ? (data?.dictionaryItems?.mainCategories[feeCategory as FeeTypeGrouping]?.feeType as Record<
                        string,
                        FeeDictionaryItemDisplayDto
                      >)
                    : (data?.dictionaryItems?.otherCategories.categories[feeCategory as FeeTypeGrouping]
                        ?.feeType as Record<string, FeeDictionaryItemDisplayDto>)
                }
                onBlur={validateField}
                fieldName="feeName"
                errorMessage={feeErrors.feeName ?? ''}
                formHelpText="Select a fee name"
              />

              <FeeInputField
                onChange={setFeeDescription}
                value={feeDescription}
                fieldLabel="Description"
                formHelpText="Only add a description when necessary, otherwise leave blank"
                onBlur={validateField}
                fieldName="feeDescription"
                errorMessage={feeErrors.feeDescription ?? ''}
                charLimit={1023}
              />

              <FeeDropdownSelectField
                required={true}
                value={feePaymentFrequencyType}
                onChange={setFeePaymentFrequencyType}
                dictionaryItems={
                  data?.dictionaryItems?.feePaymentFrequencyType as Record<string, FeeDictionaryItemDisplayDto>
                }
                fieldLabel="Payment frequency"
                onBlur={validateField}
                fieldName="feePaymentFrequencyType"
                formHelpText="Select how often this fee should be paid"
                errorMessage={feeErrors.feePaymentFrequencyType ?? ''}
              />

              <FeeRadioGroupField
                value={feeRequirementType}
                onChange={setFeeRequirementType}
                enums={data?.dictionaryItems?.feeRequirementType as Record<string, FeeDictionaryItemDisplayDto>}
                label="Is this fee required?"
                required={true}
              />

              <FeeRadioGroupField
                orientation="horizontal"
                value={feeRefundableType}
                onChange={setFeeRefundableType}
                enums={data?.dictionaryItems?.feeRefundableType as Record<string, FeeDictionaryItemDisplayDto>}
                label="Is this fee refundable?"
                required={false}
              />

              <FeeDropdownSelectField
                value={feeFormat}
                onChange={handleFeeFormatChange}
                dictionaryItems={
                  data?.dictionaryItems?.feeCalculationType as Record<string, FeeDictionaryItemDisplayDto>
                }
                fieldLabel="Fee format"
                required={true}
                onBlur={validateField}
                fieldName="feeFormat"
                errorMessage={feeErrors.feeFormat ?? ''}
                formHelpText="Select how the fee amount should be shown. Ex: A flat fee of $50, or a range of $25–$45."
              />

              <FeeAmountField
                setFeeAmountMin={setFeeAmountMin}
                setFeeAmountMax={setFeeAmountMax}
                valueMin={feeAmountMin}
                valueMax={feeAmountMax}
                feeFormat={
                  (data?.dictionaryItems?.feeCalculationType[
                    feeFormat as FeeCalculationType
                  ] as FeeCalculationDictionaryItemDisplayDto) ??
                  ('FIXED' as unknown as FeeCalculationDictionaryItemDisplayDto)
                }
                feeFormatName={feeFormat as FeeCalculationType}
                onBlur={validateField}
                feeErrors={feeErrors}
              />
            </div>
          </Form>
        </Box>
      }
      footer={
        <ModalFooter
          submitText="Save"
          onSubmit={onSubmit}
          onCancel={() => onClose()}
          isSubmitting={isCreatingFee || isUpdatingFee}
          isSubmitDisabled={!canSubmit}
        />
      }
      size={{ base: 'fullScreen', md: 'md' }}
      useDismissProps={{
        escapeKey: true,
        outsidePress: false,
      }}
      data-testid="SingleFamilyCostsAndFeesModal"
    />
  );
};

export default SingleFamilyCostsAndFeesModal;
